export type Scalars = {
  DateTime: string; // Date on BE, string on FE
};

export enum FollowupCadenceUnits {
  None = 'none',
  Minutes = 'minutes',
  Hours = 'hours',
  Days = 'days',
  Weeks = 'weeks',
}

export type InternalDataReferenceTypes =
  | 'pay-organizer-main-debtor'
  | 'pay-organizer-debtor-2'
  | 'pay-organizer-non-filing';

export type DocumentRequest = {
  id: string;
  creatorId: string;
  title: string;
  isFollowupEnabled: boolean;
  isArchived: boolean;
  autoAssignCreatorInWorkflows: boolean;
  autoAssignCustomerInWorkflows: boolean;
  followupCadence: number;
  followupCadenceUnits: FollowupCadenceUnits;
  isForCreator: boolean;
  internalDataReferenceType: InternalDataReferenceTypes | null;
  slug: string;
  type: 'basic' | 'income-data';
  emailNotificationsEnabled: boolean;
};

export type DocumentRequestFileIntegrations = 'pacer';
export type DocumentRequestFileIntegrationFileNames = 'debtor.txt' | 'petition.pdf' | 'creditor.txt' | 'credit.pdf';

export type DocumentRequestFile = {
  id: string;
  documentRequestId: string;
  fileName: string;
  fileDescription: string | null;
  isRequired: boolean;
  isForYourEyesOnly: boolean;
  order: number;
  internalDataType: 'income-data' | 'asset-data' | 'personal-data' | 'prompt-data' | null;
  internalDataPromptHelp: string | null;
  integrationSlug: DocumentRequestFileIntegrations | null;
  integrationFileName: DocumentRequestFileIntegrationFileNames | null;
};

export enum DocumentRequestStatus {
  SUCCEEDED = 'succeeded',
  IN_PROGRESS = 'in-progress',
  IN_REVIEW = 'in-review',
  FAILED = 'failed',
  SKIPPED = 'skipped',
}

export enum DocumentRequestFileStatus {
  COMPLETE = 'complete',
  INCOMPLETE = 'incomplete',
  ACTION_REQUIRED = 'action-required',
}

export type DocumentRequestUser = {
  id: string;
  documentRequestId: string;
  personId: string;
  submitForReviewModalCloseForClient: boolean | null;
  submitForReviewModalCloseForAttorney: boolean | null;
  creatorId: string;
  status: DocumentRequestStatus;
  actionRequiredFiles: number;
  hasPermission: boolean;
  isCollaborator: boolean;
  uploadedRequiredFiles: number;
  totalRequiredFiles: number;
  uploadedFiles: number;
  totalFiles: number;
  userWorkflowId: string | null;
  createdAt: Scalars['DateTime'];
  type: 'basic' | 'income-data';
};

export type DocumentRequestUserPermissions = {
  id: string;
  documentRequestId: string;
  personId: string;
};

export type ApprovedBy = { id: string; name: string | null };
export type OldApprovedBy = string | null; // We'll migrate off this soon
export type ValidationStatus = { status: string; approvedBy: OldApprovedBy | ApprovedBy } | null;
export enum CurrencyUnknown {
  Unknown = 'Unknown',
}

export type CurrencyType = 'USD' | 'CAD' | 'EUR' | 'GBP';

export type CurrencyValue = {
  currency: CurrencyType;
  amount: number | null | CurrencyUnknown;
  text: string | null;
  isUnknown?: boolean;
};

export type MonthlyDollarValue = {
  month: string;
  income: CurrencyValue;
  expenses: CurrencyValue | null;
  useInMeansTest: boolean;
  useInCalculation: boolean;
};
export type IncomeSource =
  | 'Employment'
  | 'Social Security'
  | 'Rental Property income'
  | 'Interest & Dividends'
  | 'Disability'
  | 'Unemployment'
  | 'Retirement/Pension'
  | 'Government assistance (welfare, food stamps, etc.)'
  | 'Child support or alimony received'
  | 'Business, profession, or farm'
  | 'Other';

export type IncomeMetadata = {
  incomeSource: IncomeSource;
  payFrequency: 'Weekly' | 'Biweekly' | 'Semi-monthly' | 'Monthly' | 'Quarterly' | 'Annually';
  startDate: string | null;
  endDate: string | null;
  monthlyDollarValue: MonthlyDollarValue[];
  // YTD single paystub calculation mode fields
  calculationMode?: 'all-paystubs' | 'ytd-single';
  ytdSourceDocumentId?: string | null;
  ytdMonthsOverride?: number | null;
  applyYtdToMeansTest?: boolean;
};

export type DocumentRequestUserFile = {
  id: string;
  documentRequestUserId: string;
  documentRequestFileId: string | null;
  documentId: string | null;
  personId: string;
  fileName: string | null;
  fileDescription: string | null;
  isRequired: boolean;
  isForYourEyesOnly: boolean | null;
  status: DocumentRequestFileStatus;
  rejectionMessage: string | null;
  rejectedAt: Scalars['DateTime'] | null;
  creatorId: string;
  hasPermission: boolean;
  order: number;
  validationStatus: ValidationStatus | null;
  internalDataType: 'income-data' | 'asset-data' | 'personal-data' | 'prompt-data' | null;
  internalDataPromptHelp: string | null;
  metadata: IncomeMetadata | null;
};

export type DocumentAnalysisRaw = Record<string, unknown> | null;
export type DocumentAnalysis = {
  documentAnalysis: {
    errorMessage?: string;
    isBlurry: boolean | null;
    isExpired: boolean | null;
    matchesDocumentDescription: boolean | null;
    matchesDocumentDescriptionFailReason: string | null;
  } | null;
  summary: string | null;
} | null;
export type DocumentRequestUserFileDocument = {
  documentRequestUserFileId: string;
  documentId: string;
  documentAnalysis: DocumentAnalysis;
  documentAnalysisRaw: DocumentAnalysisRaw;
  validationStatus: ValidationStatus | null;
};

export enum DocumentStatus {
  UPLOADED = 'uploaded',
  IN_PROGRESS = 'in-progress',
  FAILED = 'failed',
}

export type Document = {
  createdAt: Scalars['DateTime'];
  creatorId: string | null;
  fileName: string;
  fileSize: number;
  hasContentPermission: boolean;
  id: string;
  isCollaborator: boolean;
  isEncrypted: boolean;
  mimeType: string;
  personId: string;
  status: DocumentStatus;
  uploadedAt: Scalars['DateTime'];
  uploadedBy: {
    name: string | null;
  };
  rank: number;
  referenceId: string | null;
  referenceType:
    | 'invoice'
    | 'payment'
    | 'custom-terms'
    | 'compiled-document-request-user'
    | 'credit-report-response'
    | 'credit-report-property-search'
    | 'credit-report-previous-bankruptcies'
    | 'credit-report-foreclusers'
    | 'user-workflow-document'
    | null;
  rejectedAt?: Scalars['DateTime'] | null;
  rejectionMessage?: string | null;
};

export type ArchiveFile = {
  path: string;
  documentId: string;
};

export type Archive = {
  id: string;
  name: string;
  documentId: string | null;
  creatorId: string;
  files: ArchiveFile[];
};

/**
 * Individual line item in a breakdown.
 */
export type BreakdownItem = {
  label: string;
  amount: number;
  id?: string;
};

/**
 * Cell value type supporting optional breakdowns.
 * Can be either a simple number or an object with total and breakdown array.
 */
export type CellValue =
  | number
  | null
  | {
      total: number;
      breakdown?: BreakdownItem[];
    };

/**
 * Paystub analysis data extracted from uploaded paystub documents.
 * Fields support both simple numeric values and breakdown structures for detailed line items.
 */
export type PaystubAnalysis = {
  amountPerPayPeriod: CellValue;
  businessExpenses: CellValue;
  domesticSupportObligations: CellValue;
  insurance: CellValue;
  mandatoryContributionsForRetirementPlans: CellValue;
  netPay: number | null;
  otherDeductions: CellValue;
  overtimePerPayPeriod: CellValue;
  payDate: string | null;
  requiredRepaymentsOfRetirementFundLoans: CellValue;
  taxMedicareAndSocialSecurityDeductions: CellValue;
  unionDues: CellValue;
  voluntaryContributionsForRetirementPlans: CellValue;
  grossPay: number | null;
  // YTD (Year-To-Date) fields - extracted from paystub YTD section
  ytdGrossPay?: number | null;
  ytdTaxMedicareAndSocialSecurityDeductions?: number | null;
  ytdInsurance?: number | null;
  ytdMandatoryContributionsForRetirementPlans?: number | null;
  ytdVoluntaryContributionsForRetirementPlans?: number | null;
  ytdRequiredRepaymentsOfRetirementFundLoans?: number | null;
  ytdUnionDues?: number | null;
  ytdBusinessExpenses?: number | null;
  ytdOtherDeductions?: number | null;
  ytdDomesticSupportObligations?: number | null;
  ytdNetPay?: number | null;
  ytdOvertimePay?: number | null;
};

export type PromptData = {
  included: Array<{
    documentId: string;
    fileName: string;
  }>;
  excluded: Array<{
    documentId: string;
    fileName: string;
  }>;
};

export type InternalData = {
  id: string;
  slug: string;
  userWorkflowId: string;
  data: unknown;
};

export type InternalDataSource = {
  id: string;
  internalDataId: string;
  referenceId: string;
  referenceType: string;
  internalData: InternalData;
};

export type MeansTestAiResponse = {
  /** @deprecated AI-specific field, no longer used in deterministic calculation */
  calculationExplanation?: string;
  /** @deprecated AI-specific field, no longer used in deterministic calculation */
  calculationExplanationHtml?: string;
  grossMonthlyIncome: number;
  alimonyAndMaintenancePayments: number;
  grossBusinessProfessionIncome: number;
  businessProfessionExpenses: number;
  netBusinessProfessionIncome: number;
  grossRentalPropertyIncome: number;
  rentalPropertyExpenses: number;
  netRentalPropertyIncome: number;
  interestDividendsAndRoyalties: number;
  unemploymentCompensation: number;
  pensionOrRetirementIncome: number;
  otherHouseholdIncome: number;
  /** Unix timestamp (milliseconds) for the end of the period month - use for reliable date operations. */
  periodEndMonthTimestamp?: number;
};

export type MeansTestResponse = {
  id: string;
  documentRequestUserId: string;
  userWorkflowId: string;
  aiRequestId: string | null;
  data: MeansTestAiResponse;
  status?: 'pending' | 'completed' | 'failed';
  aiFailedReason?: string | null;
};
